// Constants
export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_CATEGORY = 'UTILITY';
export const COMPONENT_TYPES = {
  HEADER: 'HEADER',
  BODY: 'BODY',
  FOOTER: 'FOOTER', // Smarticks: the picker's footer preview looked this up and never found it
  BUTTONS: 'BUTTONS',
};
export const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

export const findComponentByType = (template, type) =>
  template.components?.find(component => component.type === type);

export const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

export const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

export const replaceTemplateVariables = (templateText, processedParams) => {
  return templateText.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.body?.[variableKey] || `{{${variable}}}`;
  });
};

export const buildTemplateParameters = (template, hasMediaHeaderValue) => {
  const allVariables = {};

  const bodyComponent = findComponentByType(template, COMPONENT_TYPES.BODY);
  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);

  if (!bodyComponent) return allVariables;

  const templateString = bodyComponent.text;

  // Process body variables
  const matchedVariables = templateString.match(/{{([^}]+)}}/g);
  if (matchedVariables) {
    allVariables.body = {};
    matchedVariables.forEach(variable => {
      const key = processVariable(variable);
      allVariables.body[key] = '';
    });
  }

  if (hasMediaHeaderValue) {
    if (!allVariables.header) allVariables.header = {};
    allVariables.header.media_url = '';
    allVariables.header.media_type = headerComponent.format.toLowerCase();

    // For document templates, include media_name field for filename support
    if (headerComponent.format.toLowerCase() === 'document') {
      allVariables.header.media_name = '';
    }
  }

  // Smarticks: a TEXT header's {{1}} needs a send-time value too (upstream #15199 does the same).
  // The backend's build_header_params sends non-media keys as positional text parameters.
  if (
    headerComponent?.format === 'TEXT' &&
    (headerComponent.text || '').includes('{{')
  ) {
    allVariables.header = {};
    (headerComponent.text.match(/{{([^}]+)}}/g) || []).forEach(variable => {
      allVariables.header[processVariable(variable)] = '';
    });
  }

  // Process button variables. Smarticks note: `buttons` stays a POSITIONAL array with gaps — the
  // backend takes each button's WhatsApp `index` from its array position (1msg wants the absolute
  // position in the template's BUTTONS). Never compact it.
  const buttonComponents = template.components.filter(
    component => component.type === COMPONENT_TYPES.BUTTONS
  );

  buttonComponents.forEach(buttonComponent => {
    if (buttonComponent.buttons) {
      buttonComponent.buttons.forEach((button, index) => {
        // Handle URL buttons with variables
        if (button.type === 'URL' && button.url && button.url.includes('{{')) {
          const buttonVars = button.url.match(/{{([^}]+)}}/g) || [];
          if (buttonVars.length > 0) {
            if (!allVariables.buttons) allVariables.buttons = [];
            allVariables.buttons[index] = {
              type: 'url',
              parameter: '',
              url: button.url,
              variables: buttonVars.map(v => processVariable(v)),
            };
          }
        }

        // Handle copy code buttons
        if (button.type === 'COPY_CODE') {
          if (!allVariables.buttons) allVariables.buttons = [];
          allVariables.buttons[index] = {
            type: 'copy_code',
            parameter: '',
          };
        }
      });
    }
  });

  return allVariables;
};

// Smarticks: the button params array is POSITIONAL (the backend takes each WhatsApp button `index`
// from the array position), but Rails' deep_munge drops nil entries from JSON arrays — measured live on
// 2026-10-04: a URL button at position 2 arrived as index 0 and 1msg refused the send ("Button at
// index 0 must be of type QuickReply"). Replace every gap with a typed placeholder the backend skips
// (template_processor_service: no parameter and not a url) while still counting it.
export const serializeProcessedParams = params => {
  if (!params || !Array.isArray(params.buttons)) return params;
  return {
    ...params,
    buttons: Array.from(params.buttons, button => button || { type: 'static' }),
  };
};
